import React, { useEffect } from 'react';
import classnames from 'classnames';

// Types
import { UserSuggestion } from 'Shared/types/UserSuggestion.type';

// Hooks
import { useUserSuggestion } from 'Shared/utils/hooks/use-user-suggestion';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton } from 'Wpds/components/button';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpAvatar } from 'Wpds/components/avatar';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';
import CompletedUserSuggestion from '../CompletedUserSuggestion';

// Styles
import './BaseUserSuggestionItem.scss';

type Props = {
  userSuggestion: UserSuggestion;
  onClick: Function;
  btnLabel: string;
  image?: React.ReactNode;
  className?: string;
};

const BaseUserSuggestionItem = ({ userSuggestion, onClick, btnLabel, image = null, className }: Props) => {
  const { otherUser, handleUpdateLastSeenAt, handleUpdateClickedAt, handleDismissSuggestion } = useUserSuggestion({
    userSuggestion,
  });

  const isComplete = userSuggestion.completed;

  const handleOnClick = () => {
    onClick && onClick();
    handleUpdateClickedAt();
  };

  useEffect(() => {
    handleUpdateLastSeenAt();
  }, []);

  const baseClassName = 'BaseUserSuggestionItem';
  const classes = classnames(baseClassName, className, isComplete ? `${baseClassName}--completed` : '');
  const Image = () => {
    if (image || otherUser) {
      const render = otherUser ? <WpAvatar avatar={otherUser.avatar} size="xl" /> : image;
      return <div className={`${baseClassName}__body__image`}>{render}</div>;
    }
    return null;
  };

  const body = isComplete ? (
    <WpContainerBody>
      <CompletedUserSuggestion userSuggestion={userSuggestion} />
    </WpContainerBody>
  ) : (
    <WpContainerBody>
      <div className={`${baseClassName}__close-btn`}>
        <WpTooltip placement="top">
          <WpTooltipTrigger>
            <WpButton variant="tertiary" rounded onClick={handleDismissSuggestion}>
              <WpIcon name="times" />
            </WpButton>
          </WpTooltipTrigger>
          <WpTooltipContent>Dismiss</WpTooltipContent>
        </WpTooltip>
      </div>
      <div className={`${baseClassName}__body`}>
        <Image />
        <div className={`${baseClassName}__body__content-wrapper`}>
          <p className={`${baseClassName}__body__title`}>{userSuggestion.title}</p>
          <p className={`${baseClassName}__body__description`}>{userSuggestion.description}</p>
          <WpButton color="gray" onClick={handleOnClick}>
            {btnLabel}
          </WpButton>
        </div>
      </div>
    </WpContainerBody>
  );
  return <WpContainer className={`${classes}`}>{body}</WpContainer>;
};

export default BaseUserSuggestionItem;
